import { useEffect, useId, useState } from 'react';
import type { MenuCategory } from '@cracked-mug/shared';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { fieldClass } from '../../components/ui/fieldStyles';
import { cn } from '../../lib/cn';

interface CategoryEditorProps {
  open: boolean;
  category: MenuCategory | null;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    displayOrder: number;
    isActive: boolean;
    icon: string;
  }) => Promise<void>;
}

interface FormState {
  name: string;
  displayOrder: string;
  isActive: boolean;
  icon: string;
}

const EMPTY: FormState = { name: '', displayOrder: '0', isActive: true, icon: '' };

export function CategoryEditor({ open, category, onClose, onSubmit }: CategoryEditorProps) {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(
        category
          ? {
              name: category.name,
              displayOrder: String(category.displayOrder),
              isActive: category.isActive,
              icon: category.icon,
            }
          : EMPTY,
      );
      setError(null);
    }
  }, [open, category]);

  const handleSubmit = async (): Promise<void> => {
    if (form.name.trim().length === 0) {
      setError('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        displayOrder: Number.parseInt(form.displayOrder, 10) || 0,
        isActive: form.isActive,
        icon: form.icon.trim(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={category ? 'Edit category' : 'New category'}
    >
      <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
        <h2 className="font-display text-2xl">
          {category ? `Edit ${category.name}` : 'New category'}
        </h2>

        <Field label="Name">
          {(id) => (
            <input
              id={id}
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={40}
              className={fieldClass}
            />
          )}
        </Field>

        <Field label="Icon (emoji or text)" hint="Shown next to the name in the menu nav.">
          {(id) => (
            <input
              id={id}
              type="text"
              value={form.icon}
              onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
              maxLength={40}
              placeholder="☕"
              className={cn(fieldClass, 'font-mono')}
            />
          )}
        </Field>

        <Field label="Display order" hint="Lower numbers render first.">
          {(id) => (
            <input
              id={id}
              type="number"
              min={0}
              value={form.displayOrder}
              onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))}
              className={cn(fieldClass, 'w-24 font-mono')}
            />
          )}
        </Field>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            className="w-5 h-5 accent-wca-red"
          />
          <span className="text-sm font-medium">Active</span>
        </label>

        {error !== null && (
          <p className="text-sm text-wca-red bg-wca-red-light rounded-lg p-3">{error}</p>
        )}
      </div>

      <div className="px-6 pb-6 pt-3 border-t border-wca-gray-light flex justify-end gap-3">
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: (id: string) => React.ReactNode;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      {children(id)}
      {hint !== undefined && <p className="text-xs text-wca-gray-mid">{hint}</p>}
    </div>
  );
}
